/**
 * welcome-dialog.tsx — Popup chào mừng sau khi đăng ký xong (gắn trong AppLayout; logic ở lib/welcome.ts).
 *
 * Giới thiệu hệ thống, báo gói Miễn phí đã được kích hoạt kèm giá, hạn mức và tính năng — TẤT CẢ đọc từ bảng giá của server
 * (GET /billing/plans, gói "free"): không có số / chữ về gói nằm cứng trong mã; chưa tải được thì chỉ hiện câu chung.
 * Nút "Bắt đầu khám phá" → trang Bắt đầu (/onboarding). Chỉ hiện cho người vừa đăng ký (dấu "chờ chào").
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Check, Gift, Inbox, Bot, Workflow } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { BrandLogo } from '@/components/shared/brand-logo'
import { useBillingPlans } from '@/hooks/use-billing'
import { useAiCredits } from '@/hooks/use-ai-credits'
import { tokenRateOf } from '@/lib/ai-credits'
import { buildPlans } from '@/pages/billing/plans'
import { i18n } from '@/lib/i18n'
import { ONBOARDING_PATH } from '@/lib/onboarding'
import { WELCOME_PLAN_ID, clearWelcomePending, isWelcomePending, planLimits } from '@/lib/welcome'
import { formatVnd } from '@/lib/format-money'
import { useAuthStore } from '@/stores/auth-store'

const PILLARS = [
  { icon: Inbox, get title() { return i18n.t('sharedMisc:welcomeDialog.pillars.inbox.title') }, get text() { return i18n.t('sharedMisc:welcomeDialog.pillars.inbox.text') } },
  { icon: Bot, get title() { return i18n.t('sharedMisc:welcomeDialog.pillars.ai.title') }, get text() { return i18n.t('sharedMisc:welcomeDialog.pillars.ai.text') } },
  { icon: Workflow, get title() { return i18n.t('sharedMisc:welcomeDialog.pillars.automation.title') }, get text() { return i18n.t('sharedMisc:welcomeDialog.pillars.automation.text') } },
]

export function WelcomeDialog() {
  const { t } = useTranslation('sharedMisc')
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const [closed, setClosed] = useState(false)
  const pending = !!user && !user.impersonatedBy && !user.platformActorId && isWelcomePending(user.id)
  const plans = useBillingPlans()
  // Lượt AI tặng của gói (chưa có ví → ẩn dòng này). Chỉ tải khi hộp chào sắp hiện.
  const { data: credits } = useAiCredits(pending && !closed)
  const free = plans.data ? buildPlans(plans.data).find((p) => p.id === WELCOME_PLAN_ID) : undefined

  if (!user || !pending || closed) return null

  const finish = (goOnboarding: boolean) => {
    clearWelcomePending(user.id)
    setClosed(true)
    if (goOnboarding) navigate(ONBOARDING_PATH)
  }
  const limits = planLimits(free, tokenRateOf(credits))

  return (
    <Dialog open onOpenChange={(open) => { if (!open) finish(false) }}>
      {/* Không tự focus nút cuối (trình duyệt sẽ cuộn hộp xuống, mất logo + lời chào ở đầu khi màn thấp). */}
      <DialogContent className="max-w-xl gap-5 p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="rounded-t-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pb-4 pt-6">
          <BrandLogo imgClassName="h-9" />
          <DialogHeader className="mt-4">
            <DialogTitle className="text-xl">
              {user.fullName ? t('welcomeDialog.greetingNamed', { name: user.fullName }) : t('welcomeDialog.greetingGeneric')}
            </DialogTitle>
            <DialogDescription>
              {t('welcomeDialog.description')}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-5 px-6">
          <ul className="grid gap-2 sm:grid-cols-3 sm:gap-3">
            {PILLARS.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex items-center gap-2.5 rounded-lg border bg-muted/30 p-3 sm:block">
                <Icon className="h-5 w-5 shrink-0 text-primary" />
                <div className="min-w-0 sm:mt-2">
                  <p className="text-sm font-medium">{title}</p>
                  {/* Màn hẹp chỉ giữ tiêu đề để khối gói Free hiện sớm hơn. */}
                  <p className="mt-1 hidden text-xs text-muted-foreground sm:block">{text}</p>
                </div>
              </li>
            ))}
          </ul>

          <PlanBlock plan={free} limits={limits} loading={plans.isLoading} />
        </div>

        <DialogFooter className="sticky bottom-0 border-t bg-background px-6 py-4">
          <Button size="lg" onClick={() => finish(true)}>
            {t('welcomeDialog.startExploring')} <ArrowRight className="h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Khối "gói đã kích hoạt": mọi chữ về gói lấy từ `plan` (server). Đang tải → khung xám; lỗi → chỉ một câu chung, không số liệu. */
function PlanBlock({ plan, limits, loading }: { plan: ReturnType<typeof buildPlans>[number] | undefined; limits: ReturnType<typeof planLimits>; loading: boolean }) {
  const { t } = useTranslation('sharedMisc')
  if (loading) return <div className="h-32 animate-pulse rounded-xl border bg-muted/40" aria-busy="true" />
  if (!plan) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-success/30 bg-success/5 p-4">
        <Gift className="h-4 w-4 text-success" />
        <p className="text-sm font-semibold">{t('welcomeDialog.planActivatedGeneric')}</p>
      </div>
    )
  }
  return (
    <div className="rounded-xl border border-success/30 bg-success/5 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Gift className="h-4 w-4 text-success" />
        <p className="text-sm font-semibold">{t('welcomeDialog.planActivatedNamed', { plan: plan.name })}</p>
        {plan.monthlyPrice != null && (
          <span className="ml-auto text-sm font-semibold tabular-nums">
            {formatVnd(plan.monthlyPrice)}<span className="text-xs font-normal text-muted-foreground">{t('welcomeDialog.perMonth')}</span>
          </span>
        )}
      </div>
      {plan.description && <p className="mt-0.5 text-xs text-muted-foreground">{plan.description}</p>}
      {limits.length > 0 && (
        // Ô số liệu cùng cỡ, tự xuống dòng và co giãn đều theo bề rộng (5 ô: 2–3 hàng tuỳ màn hình).
        <ul className="mt-3 flex flex-wrap gap-2">
          {limits.map((l) => (
            <li key={l.key} className="min-w-[7.5rem] flex-1 basis-[7.5rem] rounded-lg border border-success/20 bg-background/70 px-3 py-2 text-center">
              <p className="text-lg font-semibold leading-tight tabular-nums">{l.value}</p>
              <p className="mt-0.5 text-[11px] leading-tight text-muted-foreground">{l.label}</p>
            </li>
          ))}
        </ul>
      )}
      {plan.features.length > 0 && (
        <div className="mt-3 border-t border-dashed pt-3">
          <p className="text-center text-sm font-medium">{t('welcomeDialog.featuresTitle')}</p>
          {/* Dòng mở đầu do super-admin đặt cho gói (nếu có), vd "Tất cả trong gói Cơ bản, thêm:". */}
          {plan.featuresIntro && <p className="mt-0.5 text-center text-xs text-muted-foreground">{plan.featuresIntro}</p>}
          <div className="mb-2" />
          {/* Thẻ nhỏ tự xuống dòng theo bề rộng; hàng cuối căn giữa cho cân với màn hẹp / rộng. */}
          <ul className="flex flex-wrap justify-center gap-1.5">
            {plan.features.map((f) => (
              <li
                key={f}
                className="inline-flex max-w-full items-center gap-1 rounded-full border border-success/30 bg-background px-2.5 py-1 text-xs leading-snug text-foreground shadow-sm"
              >
                <Check className="h-3 w-3 shrink-0 text-success" />
                <span className="min-w-0 text-balance">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
